/**
 * Quest image precedence, staleness and file validation.
 *
 * Pure by design: no React, no Supabase, no clock of its own. This is the one
 * place the design's central rule lives, so it has to be the cheapest thing in
 * the codebase to falsify.
 *
 * ── THE RULE ─────────────────────────────────────────────────────────────────
 * Creator uploads and platform illustrations share ONE gallery, in the order
 * the creator arranged. An upload used to hide every illustration, which read
 * to creators as "uploading a photo erased the AI image" — adding context made
 * the page lose some. Now an illustration stays until the creator removes it
 * (quest_images DELETE is creator-permitted), and each one carries its own
 * badge so a reader can still tell our illustration from the creator's photo.
 *
 * Visibility is still DERIVED at read time — only `ready` rows render, and
 * there is no `is_visible` column to keep in sync.
 */

export interface QuestImage {
  id: string;
  questId: string;
  storagePath: string;
  source: "upload" | "generated";
  status: "pending" | "ready" | "failed";
  mediaType: string | null;
  altText: string | null;
  order: number;
  createdAt: string;
  /** Resolved public URL, attached by the adapter. */
  url?: string | null;
}

/** Mirrors the bucket's allowed_mime_types in 20260918120100. SVG is excluded
 *  deliberately: the bucket is anonymously fetchable and SVG is active
 *  content. Client and bucket must list exactly the same set — a type allowed
 *  here and refused there is an upload that fails after the bytes are sent. */
export const ALLOWED_IMAGE_MIME_TYPES = ["image/webp", "image/png", "image/jpeg"] as const;

/** 5 MB, matching the bucket's file_size_limit exactly. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Matches the `upload` half of enforce_quest_image_cap(). Above it, the write
 *  fails after the upload; below it, the creator silently loses slots. */
export const MAX_UPLOADS_PER_QUEST = 6;

/**
 * How long a `pending` row may sit before the UI stops promising it.
 *
 * Deliberately restated rather than imported from the edge function: the two
 * run in different runtimes and share no module graph at build time. A test
 * pins the two values equal, which is the only thing keeping them honest — if
 * they drift, the UI either shows a skeleton for work the sweeper has already
 * given up on, or gives up on work still in flight.
 */
export const STALE_PENDING_MINUTES = 30;

export type GalleryKind = "images" | "pending" | "none";

export interface Gallery {
  kind: GalleryKind;
  images: QuestImage[];
}

/**
 * Total order for a gallery.
 *
 * The explicit `order` decides, but every row in a generated batch is inserted
 * with the same value, so creation time breaks that tie — otherwise the three
 * illustrations render in whatever order PostgREST happened to return, which
 * differs between loads. `id` is the final tiebreak so the order is total: two
 * rows written in the same millisecond must still sort the same way on every
 * render, whatever order they arrive in.
 *
 * Copies rather than sorting in place — callers pass React state arrays.
 */
export function sortImages(images: QuestImage[]): QuestImage[] {
  return [...images].sort((a, b) =>
    a.order - b.order ||
    Date.parse(a.createdAt) - Date.parse(b.createdAt) ||
    a.id.localeCompare(b.id)
  );
}

function isStale(image: QuestImage, now: Date): boolean {
  const age = now.getTime() - Date.parse(image.createdAt);
  return age > STALE_PENDING_MINUTES * 60_000;
}

/**
 * Decide what the detail page renders, in one pass.
 *
 * Three outcomes, in strict order of preference:
 *
 *   images     — every READY row, uploads and illustrations together, in
 *                sortImages order. The first is the hero.
 *   pending    — skeletons, but ONLY while the run could still land. A dead
 *                run leaves `pending` rows forever, and a permanent skeleton
 *                promises the reader something that is never coming.
 *   none       — render no gallery at all. Not an error state: a quest with no
 *                pictures is a normal quest.
 *
 * Only READY rows render. An upload mid-flight, or one that failed, has no
 * bytes a reader could see.
 *
 * `now` is injected rather than read, so staleness is testable without fake
 * timers.
 */
export function selectGallery(images: QuestImage[], now: Date = new Date()): Gallery {
  const ready = images.filter((i) => i.status === "ready");
  if (ready.length > 0) return { kind: "images", images: sortImages(ready) };

  const pending = images.filter((i) => i.status === "pending" && !isStale(i, now));
  if (pending.length > 0) return { kind: "pending", images: sortImages(pending) };

  return { kind: "none", images: [] };
}

export type FileRejection = "too_large" | "bad_type" | "empty";

/**
 * The one picture that stands for the quest in small places (the share
 * dialog's preview card). Only a READY upload or generated image qualifies:
 * a `pending` row has no bytes behind its URL yet.
 */
export function heroImageUrl(images: QuestImage[]): string | null {
  const gallery = selectGallery(images);
  if (gallery.kind !== "images") return null;
  return gallery.images[0]?.url ?? null;
}

/**
 * The `order` a new upload takes: one past the highest in the quest, across
 * BOTH sources. A new photo adds to the gallery; it never takes the hero from
 * whatever the creator already put first. Leaving the column at its default
 * of 0 would tie with the first illustration and let insert time decide.
 */
export function nextImageOrder(images: Pick<QuestImage, "order">[]): number {
  return images.reduce((max, i) => Math.max(max, i.order), -1) + 1;
}

export type FileValidation = { ok: true } | { ok: false; reason: FileRejection };

/**
 * Reject a file before it is uploaded.
 *
 * This is a courtesy, not a control — the bucket enforces the same two limits
 * server-side, so a hand-rolled request is refused regardless. The point is to
 * fail in the picker instead of after 5 MB have crossed the wire.
 *
 * The size check is `>`, not `>=`: the bucket rejects ABOVE its limit, so a
 * client rejecting AT the limit would refuse files the server would take.
 */
export function validateImageFile(file: { size: number; type: string }): FileValidation {
  if (file.size <= 0) return { ok: false, reason: "empty" };
  if (file.size > MAX_IMAGE_BYTES) return { ok: false, reason: "too_large" };
  if (!(ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, reason: "bad_type" };
  }
  return { ok: true };
}
